import React, { useEffect } from "react";

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toolbar } from "primereact/toolbar";
import { Path } from "react-hook-form";
import { T } from "services/lang/LanguageService";
import timeout from "utils/timeout";
import { normalizeString } from "utils/utils";
import { findObjectValue } from "../utils/utils";

type TableToolbarProps<T> = {
  searchData: T[];
  onSearch: (data: T[]) => void;
  searchFields: Path<T>[];
  labelBtn?: string;
  onClickBtn: () => void;
  classNameBtn?: string;
  showBtn?: boolean;
  additionalControls: React.ReactNode
};

const TableToolbar = <T,>({
  searchData,
  onSearch,
  searchFields,
  labelBtn,
  classNameBtn,
  onClickBtn,
  showBtn = true,
  additionalControls
}: TableToolbarProps<T>) => {
  return (
    <Toolbar
      start={<ToolbarSearch data={searchData} onSearch={onSearch} searchFields={searchFields} />}
      end={
        <div className="flex gap-3">
          {additionalControls}
          {showBtn && (
            <ToolbarButton label={labelBtn} className={classNameBtn} onClick={onClickBtn} />
          )}
        </div>
      }
      className="gap-3"
    />
  );
};

export default TableToolbar;

type ToolbarSearchProps<T> = {
  data: T[];
  onSearch: (data: T[]) => void;
  searchFields: Path<T>[];
};

const ToolbarSearch = <T,>({ data, onSearch, searchFields }: ToolbarSearchProps<T>) => {
  useEffect(() => {
    onSearch(data);
  }, [data, onSearch]);

  const searchObject = (searchTerm: string) => {
    if (!searchTerm) return onSearch(data);
    const search = normalizeString(searchTerm.trim()).toLowerCase();
    const searchResults = data?.reduce((results: T[], obj: T) => {
      const values = searchFields.map(s => findObjectValue(obj, s));
      values.find(value => {
        if (value instanceof Object) {
          return Object.values(value).find(v => {
            if (v && normalizeString(v.toString()).toLowerCase().includes(search)) {
              results.push(obj);
              return true;
            }
            return false;
          });
        }
        if (value && normalizeString(value.toString()).toLowerCase().includes(search)) {
          results.push(obj);
          return true;
        } 
        return false;
      });
      return results;
    }, [] as T[]);
    return onSearch(searchResults);
  };
  
  return (
    <div className="p-toolbar-group-right">
      <div className="p-inputgroup">
        <span className="p-inputgroup-addon">
          <i className="pi pi-search" />
        </span>
        <InputText
          className="p-inputtext-sm"
          onChange={e => timeout(() => searchObject(e.target.value), 300)}
          placeholder={`${T("action.search")}...`}
        />
      </div>
    </div>
  );
};

type ToolbarButtonProps = {
  label?: string;
  className?: string;
  onClick: () => void;
};

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ label, className, onClick }) => {
  return <Button type="button" icon="pi pi-plus" size="small" label={label} className={className} onClick={onClick} />;
};
